import {
	Currency,
	Form,
	Input,
	type Job,
	Popup,
	TraitementLong,
} from '@mairistem/composants';
import {
	formatMontant,
	getErrorMessage,
	getSymbol,
	Icon,
	TreeSymbol,
	UserRole,
} from '@mairistem/finances-utils';
import { isEmpty, isNil, isUndefined } from 'lodash';
import React from 'react';
import { toast } from 'react-toastify';
import { Table } from 'rsuite-table';
import type BaseFeuilleSaisie from '../../classes/feuilleSaisies/BaseFeuilleSaisie';
import ROW_HEIGHT from '../../constants/feuilleSaisie/tableProps';
import { getMontantReferenceLibelle } from '../../constants/simulation/montantReference';
import StatutMontantSimulation from '../../constants/simulation/statutMontantSimulation';
import TypeBudget from '../../constants/simulation/typeBudget';
import TypeLigneMontant from '../../constants/simulation/typeLigneMontant';
import TypePeriode, {
	PERIODES_CALCUL,
} from '../../constants/simulation/typePeriode';
import type TreeRow from '../../interfaces/feuilleSaisie/treeRow';
import type Filters from '../../interfaces/simulation/filters';
import type LigneMontantReference from '../../interfaces/simulation/ligneMontantReference';
import type MontantSimulation from '../../interfaces/simulation/montantSimulation';
import type RegleCalcul from '../../interfaces/simulation/regleCalcul';
import type Simulation from '../../interfaces/simulation/simulation';
import {
	upsertDemandeMontant,
	upsertMontant,
} from '../../services/simulation/montantSimulation';
import { calculImputations } from '../../services/simulation/simulation';
import {
	extractCodeFromKey,
	fetchData,
	getMontantColumnFromPeriode,
	getMontantImputation,
	getMontantNameFromPeriode,
} from '../../utils/feuilleSaisie/imputation';
import { getImputationLigne } from '../../utils/imputation';
import {
	getMontantSoldeRAR,
	getTotauxLigneMontantsSimulation,
} from '../../utils/montantSimulation';
import isInputDisabled from '../../utils/simulation/input';
import renderMontantText from '../../utils/simulation/montant';
import { shouldAddReport } from '../../utils/simulation/simulation';
import {
	getRowFilters,
	isRowCaduque,
	renderTreeToggle,
} from '../../utils/treeView';
import CalculPropositionModal from './Outils/CalculProposition/CalculPropositionModal';
import CreationImputationModal from './Outils/CreationImputationModal/CreationImputationModal';
import MontantsSimulationModal from './Outils/MontantsSimulationModal/MontantsSimulationModal';
import 'rsuite-table/dist/css/rsuite-table.min.css';
import './ImputationTable.css';

const TYPE_CHAMP = {
	DEMANDE: 0,
	PROPOSE: 1,
	VOTE: 2,
	CA: 3,
	RAR: 4,
	COMMENTAIRE: 5,
};

const KEY_CODE = {
	DOWN: 40,
	UP: 38,
	ENTER: 13,
};

const renderLibelleRow = (
	rowData: TreeRow,
	expandedRowKeys: string[],
	feuilleSaisie: BaseFeuilleSaisie,
) => {
	const typeRegroupementIcon =
		feuilleSaisie.nodes[(getSymbol(rowData, TreeSymbol.DEPTH) ?? 0) + 1][0]
			.icon;

	return (
		<>
			{typeRegroupementIcon && (
				<Icon
					className="typeRegroupementIcon"
					size={20}
					{...typeRegroupementIcon}
				/>
			)}
			<span
				className={[
					'noeudLibelleGroup',
					...(rowData.isordre ? ['italic'] : []),
					...(expandedRowKeys.includes(rowData?.key) ? ['bold'] : []),
				].join(' ')}
				title={rowData.libelle.join(' | ')}
			>
				{rowData.libelle.map((libelle, index) => (
					<span
						className="noeudLibelle"
						// biome-ignore lint/suspicious/noArrayIndexKey: string
						key={index}
						style={{
							...(index !== rowData.libelle.length - 1
								? { maxWidth: `${100 / rowData.libelle.length}%` }
								: { flex: 1 }),
						}}
					>
						{index !== 0 ? '| ' : ''}
						{libelle}
					</span>
				))}
			</span>
		</>
	);
};

const stopPropagationInput = (e: React.MouseEvent) => e.stopPropagation();

export interface OutilModalData {
	feuilleSaisie?: BaseFeuilleSaisie;
	filters?: Filters;
	rowData: TreeRow;
}

interface ImputationTableProps {
	data: TreeRow[];
	feuilleSaisie: BaseFeuilleSaisie;
	filters: Filters;
	handleDataChange: (data: TreeRow[]) => void;
	key: string;
	onChangeMontant: (montantObj: object, rowData: TreeRow) => void;
	onRefresh: (onlyPanelData: boolean) => void;
	setIsFocusingInput: React.Dispatch<React.SetStateAction<boolean>>;
	simulation: Simulation;
	userRole: UserRole;
	isDemandeEnabled: boolean;
	lignesMontantReferences: LigneMontantReference[];
}

const ImputationTable = ({
	data,
	feuilleSaisie,
	filters,
	handleDataChange,
	key,
	onChangeMontant,
	setIsFocusingInput,
	simulation,
	userRole = UserRole.NORMAL,
	onRefresh,
	isDemandeEnabled,
	lignesMontantReferences,
}: ImputationTableProps) => {
	const [actualLoadingRowIdentifiant, setActualLoadingRowIdentifiant] =
		React.useState<string>('');
	const [expandedRowKeys, setExpandedRowKeys] = React.useState<string[]>([]);
	const [calculatriceModalOpen, setCalculatriceModalOpen] =
		React.useState<boolean>(false);
	const [outilModalData, setOutilModalData] = React.useState<OutilModalData>();
	const [creationImputationModalOpen, setCreationImputationModalOpen] =
		React.useState<boolean>(false);
	const [montantsSimulationModalOpen, setMontantsSimulationModalOpen] =
		React.useState(false);
	const [loadingCalculatrice, setLoadingCalculatrice] =
		React.useState<boolean>(false);
	const [job, setJob] = React.useState<Job>();
	const [calcultriceCurrentRow, setCalcultriceCurrentRow] =
		React.useState<TreeRow>();

	// En gestion par service, la saisie d'un montant (et son commentaire) doit
	// toujours passer par MontantsSimulationModal (seul endroit ou un service
	// peut etre choisi) : ces cellules du TreeView sont donc desactivees
	const saisieBudgetService = Boolean(
		simulation?.exercice?.saisieBudgetService,
	);

	// Idem quand les demandes sont actives : la saisie passe obligatoirement
	// par MontantsSimulationModal
	const isSaisieModalOnly = saisieBudgetService || isDemandeEnabled;

	// Comme l'ajout de demande : le Gestionnaire calcule en periode Demande
	// (sur ses seuls services, filtres par l'API), le calcul en periode
	// Proposition reste reserve a l'Admin/ChargePilotage
	const isCalculAccessible =
		PERIODES_CALCUL.includes(simulation?.typePeriode) &&
		userRole >=
			(simulation?.typePeriode === TypePeriode.DEMANDE
				? UserRole.GESTIONNAIRE
				: UserRole.CHARGE_PILOTAGE);

	const focusField = (input: HTMLInputElement) => {
		setTimeout(() => {
			if (!montantsSimulationModalOpen) {
				input?.focus();
				input?.select();
				input?.scrollIntoView({ block: 'center' });
			}
		}, 0);
	};

	const handleExpandFocus = (expandedRow: TreeRow) => {
		setIsFocusingInput(true);

		const observer = new window.MutationObserver(() => {
			let field = null;
			switch (simulation?.typePeriode) {
				case TypePeriode.DEMANDE:
					field = TYPE_CHAMP.DEMANDE;
					break;
				case TypePeriode.VOTE:
					field = TYPE_CHAMP.VOTE;
					break;
				default:
					field = TYPE_CHAMP.PROPOSE;
					break;
			}

			const input: HTMLInputElement = document.querySelector(
				`[data-key="0-${expandedRow.keyOrder}"][data-field="${field}"]`,
			);

			focusField(input);
			setIsFocusingInput(false);

			observer.disconnect();
		});

		observer.observe(document.querySelector('.rs-table'), {
			attributes: true,
		});
	};

	const handleExpandChange = async (isOpen: boolean, expandedRow: TreeRow) => {
		// On toggle la cle dans notre liste
		setExpandedRowKeys((old) =>
			isOpen
				? [...old, expandedRow?.key]
				: old.filter((rowKey) => rowKey !== expandedRow?.key),
		);

		try {
			// On gere l'icone loading
			setActualLoadingRowIdentifiant(expandedRow?.key);

			// Permet de ne pas rappeller l'api si les donnees sont deja chargees
			if (!isOpen || expandedRow.children.length > 0) {
				// Permet de ne pas faire d'observeur si on ferme le noeud
				if (isOpen) handleExpandFocus(expandedRow);
				setActualLoadingRowIdentifiant(null);
				return;
			}

			const initialDepth = (getSymbol(expandedRow, TreeSymbol.DEPTH) ?? 0) + 1;
			const childrens = await fetchData(
				simulation,
				feuilleSaisie,
				feuilleSaisie.nodes[initialDepth + 1],
				getRowFilters(expandedRow, filters, feuilleSaisie),
				expandedRow,
			);

			let fullItem: TreeRow = {
				...expandedRow,
				children: childrens,
			};

			let parent = getSymbol(expandedRow, TreeSymbol.PARENT);

			// Tant qu'on est pas a la racine
			while (parent !== undefined) {
				fullItem = {
					...parent,
					children: parent.children.map((child) => {
						if (child.key === fullItem.key) {
							return fullItem;
						}
						return child;
					}),
				};
				parent = getSymbol(parent, TreeSymbol.PARENT);
			}

			handleDataChange(
				data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
			);
			handleExpandFocus(expandedRow);
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors du chargement du noeud'));
		} finally {
			setActualLoadingRowIdentifiant(null);
		}
	};

	const upsertLigne = (newData: any, rowData: TreeRow) => {
		const montantData = {
			...newData,
			...getImputationLigne(simulation.identifiant, rowData),
		};

		if (Object.hasOwn(newData, 'prop') || Object.hasOwn(newData, 'vote')) {
			montantData.statut = StatutMontantSimulation.ACCEPTE;
		}

		// Si on est en demande, on fait une requete sur une autre route accessible par les gestionnaires
		if (
			Object.hasOwn(newData, 'demande') ||
			Object.hasOwn(newData, 'commentaire')
		) {
			upsertDemandeMontant(montantData);
		} else {
			upsertMontant(montantData);
		}
	};

	const handleChangeMontantIndicateurCle = (
		differenceMontant: number,
		rowData: TreeRow,
	) => {
		switch (simulation.typePeriode) {
			case TypePeriode.DEMANDE:
				onChangeMontant({ demande: differenceMontant }, rowData);
				break;
			case TypePeriode.PROPOSE:
				onChangeMontant({ prop: differenceMontant }, rowData);
				break;
			case TypePeriode.VOTE:
				onChangeMontant({ vote: differenceMontant }, rowData);
				break;
			case TypePeriode.CA_CFU:
				onChangeMontant({ ca: differenceMontant }, rowData);
				break;
			default:
				break;
		}
	};

	const handleChangeMontantSimulation = (
		differenceMontant: number,
		typePeriode: TypePeriode,
		rowData: TreeRow,
		nameColumn: string,
	) => {
		// Optimisation: si la difference vaut 0, on ne fait rien
		if (differenceMontant === 0) return;

		const montant =
			getMontantImputation(rowData, typePeriode, nameColumn) +
			differenceMontant;
		handleChangeMontantIndicateurCle(differenceMontant, rowData);

		const columnMontant =
			nameColumn === 'rep' ? 'report' : getMontantNameFromPeriode(typePeriode);

		let parent = getSymbol(rowData, TreeSymbol.PARENT);

		let fullItem = {
			...rowData,
			[columnMontant]: Number(montant),
		};

		while (parent !== undefined) {
			fullItem = {
				...parent,
				[columnMontant]:
					Number(
						getMontantImputation(parent, simulation.typePeriode, nameColumn),
					) + differenceMontant,
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};
			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	// A la fermeture de MontantsSimulationModal, recale la ligne (et ses
	// parents) sur ses montants relus en base : un autre utilisateur a pu les
	// modifier pendant que la modal etait ouverte, ce que la mise a jour par
	// difference (cf handleChangeMontantSimulation) ne peut pas voir
	const handleRecalageMontantsSimulation = (
		montantsSimulation: MontantSimulation[],
		rowData: TreeRow,
	) => {
		const totaux = getTotauxLigneMontantsSimulation(
			montantsSimulation,
			simulation.typePeriode,
		);
		const columnMontant = getMontantNameFromPeriode(simulation.typePeriode);

		// Ecart, par colonne, entre le total en base et celui affiche
		const differences: Record<string, number> = {
			...(columnMontant
				? {
						[columnMontant]:
							totaux.montant -
							getMontantImputation(rowData, simulation.typePeriode),
					}
				: {}),
			report:
				totaux.report -
				getMontantImputation(rowData, simulation.typePeriode, 'rep'),
		};
		const nombreMontantModifie =
			!isUndefined(rowData.nombre_montant) &&
			rowData.nombre_montant !== montantsSimulation.length;

		if (
			Object.values(differences).every((difference) => difference === 0) &&
			!nombreMontantModifie
		) {
			return;
		}

		// Comme handleChangeMontantSimulation, le report est compris dans
		// l'indicateur cle
		handleChangeMontantIndicateurCle(
			Object.values(differences).reduce((total, d) => total + d, 0),
			rowData,
		);

		const appliquerDifferences = (ligne: TreeRow) =>
			Object.fromEntries(
				Object.entries(differences).map(([column, difference]) => [
					column,
					Number(ligne[column] ?? 0) + difference,
				]),
			);

		let parent = getSymbol(rowData, TreeSymbol.PARENT);

		let fullItem = {
			...rowData,
			...appliquerDifferences(rowData),
			...(nombreMontantModifie
				? { nombre_montant: montantsSimulation.length }
				: {}),
		};

		while (parent !== undefined) {
			fullItem = {
				...parent,
				...appliquerDifferences(parent),
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};
			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	const handleChangeMontant = async (montant: string, rowData: TreeRow) => {
		if (
			montant === rowData?.[getMontantNameFromPeriode(simulation?.typePeriode)]
		) {
			return;
		}

		let columnMontant = getMontantColumnFromPeriode(simulation?.typePeriode);
		await upsertLigne(
			{
				[columnMontant]: montant,
			},
			rowData,
		);

		const differenceMontant =
			Number(montant) - getMontantImputation(rowData, simulation.typePeriode);
		handleChangeMontantIndicateurCle(differenceMontant, rowData);

		let parent = getSymbol(rowData, TreeSymbol.PARENT);
		columnMontant = getMontantNameFromPeriode(simulation?.typePeriode);

		let fullItem = {
			...rowData,
			[columnMontant]: Number(montant),
		};

		while (parent !== undefined) {
			fullItem = {
				...parent,
				[columnMontant]:
					Number(getMontantImputation(parent, simulation.typePeriode)) +
					differenceMontant,
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};
			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	const handleChangeMontantReport = async (
		montant: string,
		rowData: TreeRow,
	) => {
		if (Number(montant) === rowData?.report) return;

		const montantData: Partial<MontantSimulation> = { rep: Number(montant) };

		switch (simulation?.typePeriode) {
			case TypePeriode.DEMANDE:
				montantData.type = TypeLigneMontant.SAI_LIB;
				break;
			case TypePeriode.PROPOSE:
			case TypePeriode.VOTE:
				montantData.type = TypeLigneMontant.PROP_DIR;
				break;
			default:
				break;
		}

		await upsertLigne(montantData, rowData);

		let parent = getSymbol(rowData, TreeSymbol.PARENT);
		const differenceMontant = Number(montant) - rowData.report;
		handleChangeMontantIndicateurCle(differenceMontant, rowData);

		let fullItem = {
			...rowData,
			report: Number(montant),
		};

		while (parent !== undefined) {
			fullItem = {
				...parent,
				report: Number(parent.report) + differenceMontant,
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};

			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	const handleChangeCommentaire = async (
		e: any,
		rowData: TreeRow,
		isFromTreeView = true,
	) => {
		const commentaire = e.currentTarget.value;

		// Optimisation: Si les valeurs sont identiques
		if (
			commentaire === rowData?.mtsi_commentaire ||
			// ou que nous n'avons pas encore de mt_simul sur la ligne et que nous ne mettons aucune valeur
			(isEmpty(commentaire) && isUndefined(rowData?.mtsi_commentaire))
		)
			return;

		// Si on vient de la treeview, on doit faire la requete pour modifier les donnees, ce qui est deja fait depuis la modal des demandes
		if (isFromTreeView) {
			const montantData: Partial<MontantSimulation> = { commentaire };

			switch (simulation?.typePeriode) {
				case TypePeriode.DEMANDE:
					montantData.type = TypeLigneMontant.SAI_LIB;
					break;
				case TypePeriode.PROPOSE:
				case TypePeriode.VOTE:
					montantData.type = TypeLigneMontant.PROP_DIR;
					break;
				default:
					break;
			}

			await upsertLigne(montantData, rowData);
		}

		let fullItem = {
			...rowData,
			mtsi_commentaire: commentaire,
		};

		let parent = getSymbol(rowData, TreeSymbol.PARENT);

		while (parent !== undefined) {
			fullItem = {
				...parent,
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};

			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	const handleInputKeyDown = async (e: any) => {
		if (![KEY_CODE.ENTER, KEY_CODE.DOWN, KEY_CODE.UP].includes(e.keyCode)) {
			return;
		}
		const targetField = e?.currentTarget?.dataset?.field;

		const parent = e?.currentTarget.closest('.imputationTableRow');
		let nextSibling =
			e.keyCode === KEY_CODE.UP
				? parent?.previousElementSibling
				: parent?.nextElementSibling;
		let nextSiblingChevron = nextSibling?.querySelector(
			'.rs-table-cell-expand-wrapper .chevron',
		);

		// on parcours les chevrons depliés
		while (
			!isNil(nextSiblingChevron) &&
			nextSiblingChevron?.classList.contains('down')
		) {
			nextSibling =
				e.keyCode === KEY_CODE.UP
					? nextSibling?.previousElementSibling
					: nextSibling?.nextElementSibling;
			nextSiblingChevron = nextSibling?.querySelector(
				'.rs-table-cell-expand-wrapper .chevron',
			);
		}

		if (
			nextSiblingChevron &&
			(e.keyCode === KEY_CODE.DOWN || e.keyCode === KEY_CODE.ENTER)
		) {
			nextSiblingChevron?.click();

			let currentIndex = Number(parent.getAttribute('aria-rowindex')) + 1;
			const mutationObserver = new window.MutationObserver(
				(mutations, observer) => {
					// on filtre pour ne pas reagir a toutes les mutations
					const lastChange = mutations?.find((mutation) => {
						const target = mutation.target as HTMLElement;
						return target?.classList.contains('rs-table-treetable');
					});
					if (lastChange) {
						const target = document.querySelector(
							`.imputationTableRow[aria-rowindex="${currentIndex + 1}"]`,
						);
						const inputTarget: HTMLInputElement = target?.querySelector(
							`input[data-field="${targetField}"]`,
						);

						// si on a un input alors on a fini de deplier
						if (inputTarget) {
							observer.disconnect();
							focusField(inputTarget);
						} else if (!isNil(target)) {
							const targetChevron: HTMLInputElement = target.querySelector(
								'.rs-table-cell-expand-wrapper .chevron',
							);
							targetChevron?.click();
							currentIndex += 1;
						}
					}
				},
			);

			mutationObserver.observe(document.querySelector('.rs-table'), {
				attributes: true,
			});
		} else {
			const inputTarget: HTMLInputElement = nextSibling?.querySelector(
				`input[data-field="${targetField}"]`,
			);
			focusField(inputTarget);
		}
	};

	// Avec plusieurs montants sur une ligne, la saisie dans le TreeView ne
	// saurait pas sur quel montant porter : elle passe obligatoirement par
	// MontantsSimulationModal, que les demandes soient actives ou non
	const isMultiMontant = (rowData: TreeRow) => rowData.nombre_montant > 1;

	// La modal est accessible en gestion par service ou quand les demandes sont
	// actives (seul point de saisie), sur une ligne APCP, ou sinon des que la
	// ligne porte plusieurs montants (consultation, avec une seule ligne sans
	// origine creable, cf MontantsSimulationModal)
	const isMontantsSimulationModalAccessible = (rowData: TreeRow) =>
		isSaisieModalOnly ||
		rowData?.has_idsi_pro_imputations ||
		isMultiMontant(rowData);

	const renderCommentaireCell = (rowData: TreeRow) => {
		const disabled =
			isInputDisabled(
				simulation.transfertCpta,
				simulation.typePeriode,
				userRole,
				true,
			) ||
			isMultiMontant(rowData) ||
			rowData?.has_idsi_pro_imputations === true ||
			isSaisieModalOnly;

		const keyOrder = rowData?.keyOrder;

		return (
			<Input
				title={rowData?.mtsi_commentaire}
				disabled={disabled}
				key={rowData.index}
				onBlur={(e) => handleChangeCommentaire(e, rowData)}
				value={
					isMultiMontant(rowData) ? 'Voir le détail' : rowData?.mtsi_commentaire
				}
			>
				<input
					data-field={TYPE_CHAMP.COMMENTAIRE}
					data-key={keyOrder}
					data-testid={`commentaireInput${keyOrder}`}
					maxLength={255}
					onClick={stopPropagationInput}
					onKeyDown={handleInputKeyDown}
				/>
			</Input>
		);
	};

	const renderMontantInput = (rowData: TreeRow, typeChamp: number) => {
		const keyOrder = rowData?.keyOrder;
		const montant = getMontantImputation(rowData, simulation?.typePeriode);

		let disabled =
			isInputDisabled(
				simulation.transfertCpta,
				simulation.typePeriode,
				userRole,
				typeChamp === TYPE_CHAMP.DEMANDE,
			) ||
			(rowData?.imp_caduque && montant === 0) ||
			isMultiMontant(rowData) ||
			rowData?.has_idsi_pro_imputations === true ||
			isSaisieModalOnly;

		if (typeChamp === TYPE_CHAMP.CA) {
			disabled = disabled || rowData?.isordre;
			if (
				rowData?.cha_section === 'F' &&
				!simulation?.exercice?.budReportFonc
			) {
				disabled = true;
			}
		}

		return (
			<Form>
				<Currency
					disabled={disabled}
					onBlur={(_e, montantData: string) =>
						handleChangeMontant(montantData, rowData)
					}
					value={montant}
				>
					<input
						className="currency"
						data-field={typeChamp}
						data-key={keyOrder}
						data-testid={`montantInput${keyOrder}`}
						onClick={stopPropagationInput}
						onKeyDown={handleInputKeyDown}
					/>
				</Currency>
			</Form>
		);
	};

	const renderMontantReportCell = (rowData: TreeRow) => {
		if (
			isUndefined(rowData?.nombre_montant) ||
			(rowData?.cha_section === 'F' && !simulation?.exercice?.budReportFonc) ||
			rowData?.isordre
		) {
			return renderMontantText(
				rowData.report,
				expandedRowKeys.includes(rowData.key),
			);
		}

		const disabled =
			isInputDisabled(
				simulation.transfertCpta,
				simulation.typePeriode,
				userRole,
			) ||
			(rowData?.imp_caduque && rowData.report === 0) ||
			isMultiMontant(rowData) ||
			rowData?.has_idsi_pro_imputations === true ||
			isSaisieModalOnly;

		const keyOrder = rowData?.keyOrder;

		return (
			<Form>
				<Currency
					disabled={disabled}
					onBlur={(_e, montant: string) =>
						handleChangeMontantReport(montant, rowData)
					}
					value={rowData.report}
				>
					<input
						className="currency"
						data-field={TYPE_CHAMP.RAR}
						data-key={keyOrder}
						data-testid={`montantReport${keyOrder}`}
						onClick={stopPropagationInput}
						onKeyDown={handleInputKeyDown}
					/>
				</Currency>
			</Form>
		);
	};

	const handleOpenCalculatriceModal = async (rowData: TreeRow) => {
		setOutilModalData({
			rowData,
			filters,
			feuilleSaisie,
		});
		setCalculatriceModalOpen(true);
	};

	const handleOpenCreateImputationModal = async (rowData: TreeRow) => {
		setOutilModalData({
			rowData,
			filters,
			feuilleSaisie,
		});

		setCreationImputationModalOpen(true);
	};

	const handleOpenMontantsSimulationModal = async (rowData: TreeRow) => {
		setOutilModalData({ rowData });

		setMontantsSimulationModalOpen(true);
	};

	const handleMontantsSimulationModalClose = () => {
		setMontantsSimulationModalOpen(false);
	};

	const handleCreateImputationModalClose = () =>
		setCreationImputationModalOpen(false);

	const handleCalculatriceModalClose = () => setCalculatriceModalOpen(false);

	const handleCalculatriceModalValidate = async (
		row: TreeRow,
		regleCalcul: RegleCalcul,
	) => {
		try {
			setLoadingCalculatrice(true);
			setCalcultriceCurrentRow(row);
			const newJob = await calculImputations(
				simulation.identifiant,
				getRowFilters(row, filters, feuilleSaisie),
				{
					codeFeuille: feuilleSaisie.code,
					identifiantRegleCalcul: regleCalcul.identifiant,
					montantForfaitaire: regleCalcul.forfaitaire,
					pourcentageMajoration: regleCalcul.pourcentage,
					typeArrondi: regleCalcul.typeArrondi,
					typePeriode: simulation.typePeriode,
				},
			);

			setJob(newJob);
		} catch (e) {
			toast.error(getErrorMessage(e, 'Erreur lors du calcul'));
			setLoadingCalculatrice(false);
			setCalculatriceModalOpen(false);
		}
	};

	const renderMontantsSimulationModalIcon = (rowData: TreeRow) =>
		!isUndefined(rowData?.nombre_montant) &&
		isMontantsSimulationModalAccessible(rowData) && (
			<Popup
				on="hover"
				content="Accéder au détail de la ligne"
				trigger={
					<Icon
						cursor="pointer"
						data-testid={`montants-simulation-${rowData.key}`}
						iconSet="Lucide"
						name="ListTodo"
						onClick={() => handleOpenMontantsSimulationModal(rowData)}
					/>
				}
			/>
		);

	// Sur une ligne caduque, seul le detail de la ligne reste accessible (aux
	// memes conditions), sans creation de demande (cf
	// MontantsSimulationModalContent)
	const renderOutilCell = (rowData: TreeRow) =>
		isRowCaduque(rowData) ? (
			renderMontantsSimulationModalIcon(rowData)
		) : (
			<>
				{isCalculAccessible && (
					<Icon
						cursor="pointer"
						data-testid={`calculatrice${rowData.key}`}
						iconSet="Lucide"
						name="Calculator"
						onClick={() => handleOpenCalculatriceModal(rowData)}
					/>
				)}
				{simulation?.typePeriode === TypePeriode.PROPOSE &&
					userRole > UserRole.GESTIONNAIRE &&
					isUndefined(rowData?.nombre_montant) && (
						<Icon
							cursor="pointer"
							data-testid={`creation-imputation-${rowData.key}`}
							iconSet="Lucide"
							name="GitPullRequestCreate"
							onClick={() => handleOpenCreateImputationModal(rowData)}
						/>
					)}
				{renderMontantsSimulationModalIcon(rowData)}

				{lignesMontantReferences.length - colonneInfoCount > 0 && (
					<Popup
						content={
							<div>
								{lignesMontantReferences
									?.slice(colonneInfoCount, lignesMontantReferences.length)
									?.map((column) => (
										<div key={column.identifiant}>
											<strong>
												{getMontantReferenceLibelle(
													column?.typeMontant,
													simulation.exercice.anneeExercice,
												)}
											</strong>
											: {formatMontant(rowData[column?.typeMontant])}
										</div>
									))}
							</div>
						}
						trigger={
							<Icon
								cursor="pointer"
								className="info-montants"
								iconSet="Lucide"
								name="Info"
							/>
						}
					/>
				)}
			</>
		);

	const handleRowClick = (rowData: TreeRow) => {
		// Si je ne suis pas sur une ligne finale
		if (isUndefined(rowData.nombre_montant)) {
			return;
		}

		// On cherche l'input de notre periode
		const field = simulation?.typePeriode;

		const keyOrder = rowData?.keyOrder;
		const currentTarget: HTMLInputElement = document.querySelector(
			`input[data-field="${field}"][data-key="${keyOrder}"]`,
		);

		focusField(currentTarget);
	};

	const handleCreateImputation = async () => {
		setCreationImputationModalOpen(false);
		onRefresh?.(false);
	};

	const updateNombreMontant = (rowData: TreeRow, delta: number) => {
		let parent = getSymbol(rowData, TreeSymbol.PARENT);

		let fullItem = {
			...rowData,
			nombre_montant: rowData.nombre_montant + delta,
		};

		while (parent !== undefined) {
			fullItem = {
				...parent,
				children: parent.children?.map((child) => {
					if (child.key === fullItem.key) {
						return fullItem;
					}
					return child;
				}),
			};
			parent = getSymbol(parent, TreeSymbol.PARENT);
		}

		handleDataChange(
			data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
		);
	};

	const handleAddMontant = (rowData: TreeRow, nombre = 1) =>
		updateNombreMontant(rowData, nombre);

	const handleRemoveMontant = (rowData: TreeRow) =>
		updateNombreMontant(rowData, -1);

	const handleCloseJobCalculatrice = async (finishedJob: Job) => {
		setLoadingCalculatrice(false);
		setJob(null);

		if (finishedJob.status === 1) {
			// on recharge l'element courant et ses freres afin de mettre a jour les montants
			let parent = getSymbol(calcultriceCurrentRow, TreeSymbol.PARENT);

			const initialDepth =
				getSymbol(calcultriceCurrentRow, TreeSymbol.DEPTH) ?? 0;
			const siblings = await fetchData(
				simulation,
				feuilleSaisie,
				feuilleSaisie.nodes[initialDepth + 1],
				getRowFilters(parent, filters, feuilleSaisie),
				calcultriceCurrentRow,
			);

			const rowCode = extractCodeFromKey(calcultriceCurrentRow.key);
			const newRow = siblings.find(
				(sibling) => extractCodeFromKey(sibling.key) === rowCode,
			);
			// le calcul alimente le demande, et aussi le propose en periode Proposition
			const differenceDemande =
				newRow.montant_demande - calcultriceCurrentRow.montant_demande;
			const differencePropose =
				newRow.montant_propose - calcultriceCurrentRow.montant_propose;

			let fullItem = {
				...calcultriceCurrentRow,
				montant_demande: newRow.montant_demande,
				montant_propose: newRow.montant_propose,
				// le calcul peut creer des demandes (une par service)
				nombre_montant: newRow.nombre_montant,
				// si row.children est null, on est sur un noeud final
				children: isNil(calcultriceCurrentRow.children) ? null : [],
			};

			// on met a jour le total de la ligne courante
			while (parent !== undefined) {
				fullItem = {
					...parent,
					montant_demande:
						Number(getMontantImputation(parent, TypePeriode.DEMANDE)) +
						differenceDemande,
					montant_propose:
						Number(getMontantImputation(parent, TypePeriode.PROPOSE)) +
						differencePropose,
					children: parent.children?.map((child) => {
						if (child.key === fullItem.key) {
							return fullItem;
						}
						return child;
					}),
				};
				parent = getSymbol(parent, TreeSymbol.PARENT);
			}

			handleDataChange(
				data.map((imput) => (imput?.key === fullItem?.key ? fullItem : imput)),
			);
			setExpandedRowKeys((old) =>
				old.filter((rowKey) => rowKey !== calcultriceCurrentRow?.key),
			);
			await onRefresh?.(true);
			setCalculatriceModalOpen(false);
		} else {
			toast.error(finishedJob.resultat);
		}
	};

	const handleJobErrorCalculatrice = () => {
		setJob(null);
		setLoadingCalculatrice(false);
		toast.error('Erreur lors du traitement');
	};

	const colonneInfoCount =
		simulation?.budget?.type === TypeBudget.COMPTE_ADMINISTRATIF ? 3 : 2;

	return (
		<>
			<Table<TreeRow, string>
				autoHeight
				data={data}
				expandedRowKeys={expandedRowKeys}
				hover={false}
				loading={data.length === 0}
				isTree
				key={key}
				onExpandChange={handleExpandChange}
				onRowClick={handleRowClick}
				renderTreeToggle={(_expandButton, rowData, isExpanded) =>
					renderTreeToggle(rowData, isExpanded, actualLoadingRowIdentifiant)
				}
				rowKey="key"
				rowClassName="imputationTableRow"
				rowHeight={ROW_HEIGHT}
				wordWrap={false}
			>
				{({ Cell, Column, HeaderCell }) => (
					<>
						<Column flexGrow={4} fullText verticalAlign="middle">
							<HeaderCell>Ligne budgétaire</HeaderCell>
							<Cell dataKey="libelle">
								{(rowData) =>
									renderLibelleRow(rowData, expandedRowKeys, feuilleSaisie)
								}
							</Cell>
						</Column>

						{lignesMontantReferences
							?.slice(0, colonneInfoCount)
							?.map((column) => (
								<Column
									align="right"
									flexGrow={1}
									verticalAlign="middle"
									key={column?.typeMontant}
								>
									<HeaderCell>
										{getMontantReferenceLibelle(
											column?.typeMontant,
											simulation.exercice.anneeExercice,
										)}
									</HeaderCell>
									<Cell dataKey={column?.typeMontant}>
										{(rowData) =>
											renderMontantText(
												rowData[column?.typeMontant],
												expandedRowKeys.includes(rowData.key),
											)
										}
									</Cell>
								</Column>
							))}

						{feuilleSaisie.isCreditReporteShown &&
							shouldAddReport(simulation, feuilleSaisie) && (
								<Column align="right" flexGrow={1} verticalAlign="middle">
									<HeaderCell>
										<span className="bold">{`RAR ${simulation.exercice.anneeExercice - 1}`}</span>
									</HeaderCell>
									<Cell dataKey="montantReport">
										{(rowData) => renderMontantReportCell(rowData)}
									</Cell>
								</Column>
							)}

						{simulation?.typePeriode === TypePeriode.DEMANDE && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell>
									<span className="bold">{`Demandes ${simulation.exercice.anneeExercice}`}</span>
								</HeaderCell>
								<Cell dataKey="montant_demande">
									{(rowData) =>
										isUndefined(rowData?.nombre_montant)
											? renderMontantText(
													getMontantImputation(rowData, TypePeriode.DEMANDE),
													expandedRowKeys.includes(rowData.key),
												)
											: renderMontantInput(rowData, TYPE_CHAMP.DEMANDE)
									}
								</Cell>
							</Column>
						)}

						{[TypePeriode.PROPOSE, TypePeriode.VOTE]?.includes(
							simulation?.typePeriode,
						) && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell>
									<span
										className={
											simulation.typePeriode === TypePeriode.PROPOSE && 'bold'
										}
									>
										{`Proposé ${simulation.exercice.anneeExercice}`}
									</span>
								</HeaderCell>
								<Cell dataKey="montant_propose">
									{(rowData) =>
										!isUndefined(rowData?.nombre_montant) &&
										simulation?.typePeriode === TypePeriode.PROPOSE
											? renderMontantInput(rowData, TYPE_CHAMP.PROPOSE)
											: renderMontantText(
													getMontantImputation(rowData, TypePeriode.PROPOSE),
													simulation.typePeriode === TypePeriode.PROPOSE &&
														expandedRowKeys.includes(rowData.key),
												)
									}
								</Cell>
							</Column>
						)}

						{simulation?.typePeriode === TypePeriode.VOTE && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell>
									<span className="bold">{`Voté ${simulation.exercice.anneeExercice}`}</span>
								</HeaderCell>
								<Cell dataKey="montant_vote">
									{(rowData) =>
										!isUndefined(rowData?.nombre_montant)
											? renderMontantInput(rowData, TYPE_CHAMP.VOTE)
											: renderMontantText(
													getMontantImputation(rowData, TypePeriode.VOTE),
													expandedRowKeys.includes(rowData.key),
												)
									}
								</Cell>
							</Column>
						)}

						{simulation?.typePeriode === TypePeriode.CA_CFU && (
							<Column align="right" flexGrow={1} verticalAlign="middle">
								<HeaderCell>
									<span className="bold">Reste à réaliser</span>
								</HeaderCell>
								<Cell dataKey="montantCaCfu">
									{(rowData) =>
										!isUndefined(rowData?.nombre_montant)
											? renderMontantInput(rowData, TYPE_CHAMP.CA)
											: renderMontantText(
													getMontantImputation(rowData, TypePeriode.CA_CFU),
													expandedRowKeys.includes(rowData.key),
												)
									}
								</Cell>
							</Column>
						)}

						{[
							TypeBudget.BUDGET_PRIMITIF,
							TypeBudget.BUDGET_SUPPLEMENTAIRE,
						]?.includes(simulation?.budget?.type) &&
							[TypePeriode.PROPOSE, TypePeriode.VOTE]?.includes(
								simulation?.typePeriode,
							) && (
								<Column align="center" flexGrow={1} verticalAlign="middle">
									<HeaderCell>
										{`RAR ${simulation.exercice.anneeExercice - 1} + `}
										<div>
											{simulation?.typePeriode === TypePeriode.PROPOSE &&
												`Proposé ${simulation.exercice.anneeExercice}`}
											{simulation?.typePeriode === TypePeriode.VOTE &&
												`Voté ${simulation.exercice.anneeExercice}`}
										</div>
									</HeaderCell>
									<Cell dataKey="soldeRAR">
										{(rowData) =>
											renderMontantText(
												getMontantSoldeRAR(rowData, simulation),
												expandedRowKeys.includes(rowData.key),
											)
										}
									</Cell>
								</Column>
							)}

						<Column flexGrow={2} verticalAlign="middle">
							<HeaderCell>Commentaires</HeaderCell>
							<Cell className="commentaireCell" dataKey="commentaire">
								{(rowData) =>
									!isUndefined(rowData?.nombre_montant) &&
									renderCommentaireCell(rowData)
								}
							</Cell>
						</Column>
						<Column align="end" flexGrow={1} verticalAlign="middle">
							{/* @ts-expect-error */}
							<HeaderCell />
							<Cell className="outilCell">
								{(rowData) => {
									const open =
										montantsSimulationModalOpen &&
										rowData.key === outilModalData.rowData.key;

									return (
										<>
											{renderOutilCell(rowData)}
											{open && (
												<MontantsSimulationModal
													onAddMontant={(nombre) =>
														handleAddMontant(rowData, nombre)
													}
													onChangeCommentaire={(e) =>
														handleChangeCommentaire(e, rowData, false)
													}
													onChangeMontant={(
														differenceMontant,
														typePeriode,
														nameColumn,
													) =>
														handleChangeMontantSimulation(
															differenceMontant,
															typePeriode,
															rowData,
															nameColumn,
														)
													}
													feuilleSaisie={feuilleSaisie}
													isDemandeEnabled={isDemandeEnabled}
													onClose={handleMontantsSimulationModalClose}
													onRecalageMontants={(montantsSimulation) =>
														handleRecalageMontantsSimulation(
															montantsSimulation,
															rowData,
														)
													}
													onRemoveMontant={() => handleRemoveMontant(rowData)}
													open={open}
													rowData={rowData}
													simulation={simulation}
													userRole={userRole}
												/>
											)}
										</>
									);
								}}
							</Cell>
						</Column>
					</>
				)}
			</Table>
			{calculatriceModalOpen && (
				<CalculPropositionModal
					data={outilModalData}
					onClose={handleCalculatriceModalClose}
					onValidate={handleCalculatriceModalValidate}
					open={calculatriceModalOpen}
					simulation={simulation}
				/>
			)}
			{creationImputationModalOpen && (
				<CreationImputationModal
					data={outilModalData}
					onClose={handleCreateImputationModalClose}
					open={creationImputationModalOpen}
					simulation={simulation}
					onValidate={() => handleCreateImputation()}
				/>
			)}

			{loadingCalculatrice && job && (
				<TraitementLong
					title="Calcul des imputations en cours"
					identifiantJob={job?.identifiant}
					onFinish={handleCloseJobCalculatrice}
					onError={handleJobErrorCalculatrice}
				/>
			)}
		</>
	);
};

export default ImputationTable;
